import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef, ReactNode } from "react";
import { useSelector } from "react-redux";
import requestWithSubTenantIfNeeded from "store/api/organization/requestWithSubTenantIfNeeded";
import { ffKeys, hasFeatureFlag } from "@p81-common/shell-core-js";
import { getAddonStatsByType } from "components/billing/helpers";

export interface UsedMbpsState {
  usedMbps: number;
  loading: boolean;
  error: Error | null;
  hasChinaBandwidth: boolean;
  refetch: () => Promise<void>;
}

interface UsedMbpsProviderProps {
  children: ReactNode;
}

const UsedMbpsContext = createContext<UsedMbpsState | null>(null);

/**
 * Provider that fetches used China bandwidth once and shares it with all children.
 * Wrap your page component with this to ensure only 1 API call per page render.
 */
export function UsedMbpsProvider({ children }: UsedMbpsProviderProps) {
  const value = useUsedMbpsInternal();
  return (
    <UsedMbpsContext.Provider value={value}>
      {children}
    </UsedMbpsContext.Provider>
  );
}

/**
 * Hook to fetch used China bandwidth (Mbps) for the tenant.
 * If used within UsedMbpsProvider, returns shared state (1 API call).
 * If used outside provider, fetches independently (for backwards compatibility).
 */
export function useUsedMbps(): UsedMbpsState {
  const contextValue = useContext(UsedMbpsContext);
  const independentValue = useUsedMbpsInternal(contextValue !== null);

  // If we're inside a provider, use shared context value
  // Otherwise, use independent fetch (backwards compatible)
  return contextValue ?? independentValue;
}

/**
 * Internal hook that performs the actual fetch.
 * @param skip - If true, skip fetching (used when context is available)
 */
function useUsedMbpsInternal(skip = false): UsedMbpsState {
  const [state, setState] = useState<{
    usedMbps: number;
    loading: boolean;
    error: Error | null;
  }>({
    usedMbps: 0,
    loading: true,
    error: null,
  });

  // Track mount state to prevent setState on unmounted component
  const isMountedRef = useRef(true);

  // Listen to tunnel socket events via Redux - triggers refetch when tunnels change
  const tunnelEventVersion = useSelector(
    (s: any) => s.networksBeaconsGet?.data?.tunnelEventVersion ?? 0
  );

  const tenantId = useSelector(
    (s: any) => s.rootAccountGet.data?.account?.tenantId
  );

  const accountStats = useSelector(
    (s: any) => s.accountGet?.data?.relationships?.addonStatistic
  );

  const hasChinaFF = hasFeatureFlag(ffKeys.CHINA_REGION);
  const { quantity: chinaBwQuantity = 0 } = getAddonStatsByType(
    accountStats || [],
    "chinaBandwidth"
  );
  const hasChinaAddon = chinaBwQuantity > 0;
  const hasChinaBandwidth = hasChinaFF && hasChinaAddon;

  const fetchUsedMbps = useCallback(async () => {
    if (!hasChinaFF || !hasChinaAddon || !tenantId) {
      if (isMountedRef.current) {
        setState({ usedMbps: 0, loading: false, error: null });
      }
      return;
    }

    if (isMountedRef.current) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const response = await requestWithSubTenantIfNeeded(
        "get",
        `networking/getTenantUsedMbps`
      );
      if (isMountedRef.current) {
        setState({
          usedMbps: response?.data?.total ?? 0,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        setState({
          usedMbps: 0,
          loading: false,
          error: error as Error,
        });
      }
    }
  }, [hasChinaFF, hasChinaAddon, tenantId]);

  // Track mount/unmount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch on mount, when dependencies change, or when tunnel socket event fires
  useEffect(() => {
    if (skip) {
      return;
    }

    fetchUsedMbps();
  }, [skip, fetchUsedMbps, tunnelEventVersion]);

  return useMemo(() => ({
    usedMbps: state.usedMbps,
    loading: state.loading,
    error: state.error,
    hasChinaBandwidth,
    refetch: fetchUsedMbps,
  }), [state.usedMbps, state.loading, state.error, hasChinaBandwidth, fetchUsedMbps]);
}
